import type {
  AlertZone,
  AlertZoneInput,
  AppNotification,
  AuthUser,
  CreateGroupInput,
  FeatureCollection,
  FriendshipStatus,
  Group,
  GroupComment,
  GroupInvite,
  GroupLeaderboardEntry,
  GroupMember,
  GroupPost,
  GroupReactionEmoji,
  GroupRole,
  GroupSummary,
  KnownIndividual,
  LogbookStats,
  LogEntryInput,
  NotificationPreferences,
  RegisterInput,
  SpeciesType,
  UpdateGroupInput,
  UpdateProfileInput,
} from "@podlog/shared";

const API_BASE = process.env.NEXT_PUBLIC_API_BASE ?? "http://localhost:8799";

export function getAuthToken(): string | null {
  if (typeof window === "undefined") return null;
  return localStorage.getItem("podlog_token");
}

export function setAuthToken(token: string | null) {
  if (typeof window === "undefined") return;
  if (token) localStorage.setItem("podlog_token", token);
  else localStorage.removeItem("podlog_token");
}

const MEDIA_BASE = process.env.NEXT_PUBLIC_MEDIA_BASE || "https://media.podlog.co.uk";

/** Absolute URL for an API-relative media path (e.g. /media/:key). */
export const mediaUrl = (path: string | null | undefined): string | undefined => {
  if (!path) return undefined;
  
  if (path.includes("/api/media/avatar/")) {
    const key = path.split("/api/media/avatar/")[1];
    return `${MEDIA_BASE}/avatars/${key}`;
  }
  if (path.includes("/media/assets/")) {
    const key = path.split("/media/assets/")[1];
    return `${MEDIA_BASE}/${key}`;
  }
  if (path.includes("/media/group-icon/")) {
    const key = path.split("/media/group-icon/")[1];
    return `${MEDIA_BASE}/group-icons/${key}`;
  }
  if (path.includes("/media/group-post/")) {
    const key = path.split("/media/group-post/")[1];
    return `${MEDIA_BASE}/group-posts/${key}`;
  }
  const sightingMatch = path.match(/\/media\/([^/]+)$/);
  if (sightingMatch) {
    return `${MEDIA_BASE}/sightings/${sightingMatch[1]}`;
  }

  if (path.startsWith("http")) return path;
  return `${MEDIA_BASE}${path.startsWith("/") ? "" : "/"}${path}`;
};

/**
 * True if a `media_url` points at an uploaded video file (as opposed to a
 * photo). Shared by `PodInfoDock`, `PodInfoPanel`, and `MediaGallery` so the
 * three call sites never silently diverge on which extensions count as video.
 */
export const isVideo = (url: string): boolean => /\.(mp4|webm|mov|m4v|ogg)(\?|$)/i.test(url);

async function getJson<T>(path: string): Promise<T> {
  const res = await fetch(`${API_BASE}${path}`);
  if (!res.ok) throw new Error(`${path} → ${res.status}`);
  return (await res.json()) as T;
}

export const fetchHeatmap = () => getJson<FeatureCollection>("/api/heatmap");
export const fetchRoutes = (cacheBust?: number) => {
  const qs = cacheBust ? `?_cb=${cacheBust}` : "";
  return getJson<FeatureCollection>(`/api/routes${qs}`);
};
export const fetchPods = (cacheBust?: number) => {
  const qs = cacheBust ? `?_cb=${cacheBust}` : "";
  return getJson<FeatureCollection>(`/api/pods${qs}`);
};
export const fetchSightings = (days?: number | null, cacheBust?: number) => {
  const params = new URLSearchParams();
  if (days !== undefined && days !== null) params.set("days", String(days));
  if (days === null) params.set("days", "max");
  if (cacheBust) params.set("_cb", String(cacheBust));
  const qs = params.toString();
  return getJson<FeatureCollection>(`/api/sightings${qs ? `?${qs}` : ""}`);
};

/** The map's cold-load "alive" layers in one request. The heatmap is fetched
 *  separately (it's large and must not block these smaller layers painting). */
export interface MapBootstrap {
  routes: FeatureCollection;
  pods: FeatureCollection;
  sightings: FeatureCollection;
}
export const fetchMapBootstrap = (days?: number | null, cacheBust?: number) => {
  const params = new URLSearchParams();
  if (days !== undefined && days !== null) params.set("days", String(days));
  if (days === null) params.set("days", "max");
  if (cacheBust) params.set("_cb", String(cacheBust));
  const qs = params.toString();
  return getJson<MapBootstrap>(`/api/map-bootstrap${qs ? `?${qs}` : ""}`);
};

// Bump when a loader refreshes overlay data — appended to the overlay fetch URLs
// so the browser fetches a fresh URL rather than serving a stale (possibly empty)
// cached response from before the data existed.
const OVERLAY_REV = 3;
/** Environmental overlay layer (currents/sst/chlor) as res-5 GeoJSON. */
export const fetchOverlay = (layer: string) =>
  getJson<FeatureCollection>(`/api/overlay/${encodeURIComponent(layer)}?rev=${OVERLAY_REV}`);
/** Learned tidal-hotspot cells (each carries `peak_bucket`) for the live tide pulse. */
export const fetchTidalHotspots = () =>
  getJson<FeatureCollection>(`/api/overlay/tidal-hotspots?rev=${OVERLAY_REV}`);

/** CMEMS tidal field (pure-tide height + total currents) at the forecast step
 *  nearest `atIso`. The FC carries a `valid_time` foreign member (the step used). */
export type TideFieldFC = FeatureCollection & { valid_time?: string };
export const fetchTides = (atIso?: string) =>
  getJson<TideFieldFC>(`/api/overlay/tides${atIso ? `?at=${encodeURIComponent(atIso)}` : ""}`);

const knownIndividualsCache = new Map<string, { data: KnownIndividual[]; ts: number }>();
const KI_CACHE_TTL = 86400000; // 24 hours

export async function fetchKnownIndividuals(speciesType: SpeciesType): Promise<KnownIndividual[]> {
  const cached = knownIndividualsCache.get(speciesType);
  if (cached && Date.now() - cached.ts < KI_CACHE_TTL) return cached.data;

  const data = await getJson<{ individuals: KnownIndividual[] }>(
    `/api/known-individuals?species_type=${encodeURIComponent(speciesType)}`,
  );
  knownIndividualsCache.set(speciesType, { data: data.individuals, ts: Date.now() });
  return data.individuals;
}

/** Public, privacy-gated reporter attribution. All null for guest/anonymous posts. */
export interface SightingReporter {
  is_anonymous: boolean;
  reporter_username: string | null;
  reporter_display_name: string | null;
  reporter_avatar_url: string | null;
}

export interface PodSighting extends SightingReporter {
  id: string;
  species_type: SpeciesType;
  lat: number;
  lng: number;
  timestamp: string;
  media_url: string | null;
  youtube_url: string | null;
  claimed_individual_id: string | null;
  claimed_individual_name: string | null;
}

/**
 * Group the map's sightings GeoJSON (`/api/sightings`) into per-pod `PodSighting[]`.
 * The map already downloads every sighting in the lookback window with all the fields
 * the pod info panel needs, so the panel reads from this instead of making its own
 * `/api/pods/:id/sightings` request. Sightings without a `pod_id` are skipped.
 */
export function groupSightingsByPod(fc: FeatureCollection): Map<string, PodSighting[]> {
  const byPod = new Map<string, PodSighting[]>();
  for (const f of fc.features) {
    const p = f.properties as Record<string, unknown> | null;
    const podId = p?.pod_id;
    if (!p || !podId) continue;
    const coords = (f.geometry as { coordinates?: number[] } | null)?.coordinates;
    const [lng, lat] = (coords ?? [null, null]) as [number | null, number | null];
    if (lng == null || lat == null) continue;
    const sighting: PodSighting = {
      id: String(p.id),
      species_type: p.species_type as PodSighting["species_type"],
      lat,
      lng,
      timestamp: String(p.timestamp),
      media_url: (p.media_url as string | null) ?? null,
      youtube_url: (p.youtube_url as string | null) ?? null,
      claimed_individual_id: (p.claimed_individual_id as string | null) ?? null,
      claimed_individual_name: (p.claimed_individual_name as string | null) ?? null,
      is_anonymous: Boolean(p.is_anonymous),
      reporter_username: (p.reporter_username as string | null) ?? null,
      reporter_display_name: (p.reporter_display_name as string | null) ?? null,
      reporter_avatar_url: (p.reporter_avatar_url as string | null) ?? null,
    };
    const key = String(podId);
    const list = byPod.get(key);
    if (list) list.push(sighting);
    else byPod.set(key, [sighting]);
  }
  return byPod;
}

export interface SubmitArgs {
  species_type: SpeciesType;
  turnstileToken: string;
  gps?: { lat: number; lng: number };
  onWater?: boolean;
  h3_index?: string;
  timestamp?: string;
  photo?: File | Blob | null;
  claimed_individual_id?: string;
  youtube_url?: string;
  /** Hide the reporter from the public UI (signed-in users only; guests are always anonymous). */
  anonymous?: boolean;
  is_photo_gps?: boolean;
  force_on_land?: boolean;
}

export interface SubmitResult {
  id: string;
  status: string;
  location_accuracy: string;
  confidence_score: number;
  cv_label: string | null;
}

export async function submitSighting(args: SubmitArgs): Promise<SubmitResult> {
  const fd = new FormData();
  fd.set("species_type", args.species_type);
  fd.set("turnstileToken", args.turnstileToken);
  if (args.gps) {
    fd.set("gps_lat", String(args.gps.lat));
    fd.set("gps_lng", String(args.gps.lng));
  }
  if (args.onWater != null) fd.set("onWater", String(args.onWater));
  if (args.h3_index) fd.set("h3_index", args.h3_index);
  if (args.timestamp) fd.set("timestamp", args.timestamp);
  if (args.photo) fd.set("photo", args.photo);
  if (args.claimed_individual_id) fd.set("claimed_individual_id", args.claimed_individual_id);
  if (args.youtube_url) fd.set("youtube_url", args.youtube_url);
  if (args.anonymous) fd.set("anonymous", "true");
  if (args.is_photo_gps) fd.set("is_photo_gps", "true");
  if (args.force_on_land) fd.set("force_on_land", "true");

  // credentials: "include" sends the session cookie so a signed-in submission is
  // attributed; guests simply have no cookie and post anonymously.
  // We also send the Authorization header for cross-origin setups on iOS Safari.
  const headers = new Headers();
  const token = getAuthToken();
  if (token) headers.set("Authorization", `Bearer ${token}`);

  const res = await fetch(`${API_BASE}/api/submit-sighting`, {
    method: "POST",
    body: fd,
    credentials: "include",
    headers,
  });
  const data = (await res.json()) as SubmitResult & { error?: string };
  if (!res.ok) throw new Error(data.error ?? `Submit failed (${res.status})`);
  return data;
}

export interface SightingDetails {
  id: string;
  species_type: SpeciesType;
  lat: number;
  lng: number;
  timestamp: string;
  h3_index: string;
  media_url: string | null;
  youtube_url: string | null;
  user_id: string | null;
  status: string;
}

export async function fetchSighting(id: string): Promise<SightingDetails> {
  const res = await fetch(`${API_BASE}/api/sighting/${id}`);
  if (!res.ok) throw new Error("Failed to fetch sighting");
  return res.json();
}

export interface EditSightingArgs {
  id: string;
  gps?: { lat: number; lng: number };
  onWater?: boolean;
  is_photo_gps?: boolean;
  h3_index?: string;
  photo?: Blob | null;
  youtube_url?: string;
}

export async function editSighting(args: EditSightingArgs): Promise<{ success: boolean; status: string; media_url: string | null }> {
  const fd = new FormData();
  if (args.gps) {
    fd.set("gps_lat", String(args.gps.lat));
    fd.set("gps_lng", String(args.gps.lng));
  }
  if (args.onWater != null) fd.set("onWater", String(args.onWater));
  if (args.h3_index) fd.set("h3_index", args.h3_index);
  if (args.photo) fd.set("photo", args.photo);
  if (args.youtube_url !== undefined) fd.set("youtube_url", args.youtube_url);
  if (args.is_photo_gps) fd.set("is_photo_gps", "true");

  const headers = new Headers();
  const token = getAuthToken();
  if (token) headers.set("Authorization", `Bearer ${token}`);

  const res = await fetch(`${API_BASE}/api/edit-sighting/${args.id}`, {
    method: "PATCH",
    body: fd,
    credentials: "include",
    headers,
  });
  const data = (await res.json()) as any;
  if (!res.ok) throw new Error(data.error ?? `Edit failed (${res.status})`);
  return data;
}


// ---- Logbook (cookie-authenticated) ----
export interface LogbookEntry {
  id: string;
  sighting_id: string;
  species_type: SpeciesType;
  status: string;
  lat: number;
  lng: number;
  timestamp: string;
  media_url: string | null;
  youtube_url: string | null;
  personal_notes: string | null;
  is_lifer: boolean;
  rating: number | null;
  weather: string | null;
  companions: string | null;
  equipment: string | null;
  personal_photos: string[] | null;
  created_at: string;
  updated_at: string;
}

/** Thrown so callers can distinguish "not signed in" from other failures. */
export class UnauthorizedError extends Error {}

async function authedJson<T>(path: string, init?: RequestInit): Promise<T> {
  const headers = new Headers(init?.headers);
  const token = getAuthToken();
  if (token) headers.set("Authorization", `Bearer ${token}`);

  const res = await fetch(`${API_BASE}${path}`, { 
    cache: "no-store",
    ...init, 
    headers, 
    credentials: "include" 
  });
  if (res.status === 401) throw new UnauthorizedError("Not signed in.");
  if (!res.ok) {
    const data = (await res.json().catch(() => ({}))) as { error?: string };
    throw new Error(data.error ?? `${path} → ${res.status}`);
  }
  return (await res.json()) as T;
}

const logbookCache = new Map<string, { data: any; ts: number }>();
const CACHE_TTL = 600000; // 10 minutes

export async function fetchLogbookEntries(limit?: number, offset?: number): Promise<LogbookEntry[]> {
  const params = new URLSearchParams();
  if (limit !== undefined) params.set("limit", String(limit));
  if (offset !== undefined) params.set("offset", String(offset));
  const qs = params.toString();
  
  const cacheKey = `entries:${qs}`;
  const cached = logbookCache.get(cacheKey);
  if (cached && Date.now() - cached.ts < CACHE_TTL) return cached.data;

  const data = await authedJson<{ entries: LogbookEntry[] }>(
    `/api/logbook/entries${qs ? `?${qs}` : ""}`,
  );
  logbookCache.set(cacheKey, { data: data.entries, ts: Date.now() });
  return data.entries;
}

export async function fetchLogbookStats(): Promise<LogbookStats> {
  const cached = logbookCache.get("stats");
  if (cached && Date.now() - cached.ts < CACHE_TTL) return cached.data;

  const data = await authedJson<{ stats: LogbookStats }>("/api/logbook/stats");
  logbookCache.set("stats", { data: data.stats, ts: Date.now() });
  return data.stats;
}

export async function upsertLogEntry(
  sightingId: string,
  input: LogEntryInput,
): Promise<{ id: string }> {
  // Clear logbook cache on mutation
  logbookCache.clear();
  return authedJson<{ id: string }>(`/api/logbook/entries/${sightingId}`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(input),
  });
}

export async function fetchLogbookMap(): Promise<FeatureCollection> {
  const cached = logbookCache.get("map");
  if (cached && Date.now() - cached.ts < CACHE_TTL) return cached.data;

  const data = await authedJson<FeatureCollection>("/api/logbook/map");
  logbookCache.set("map", { data, ts: Date.now() });
  return data;
}

// ---- Auth (cookie-authenticated) ----

/** GET /api/auth/me never 401s — it returns { user, unread_count }. The unread
 *  count seeds the header bell so it needs no dedicated poll on load. */
export interface MeResponse {
  user: AuthUser | null;
  unread_count: number;
}
export async function fetchMe(): Promise<MeResponse> {
  try {
    const headers = new Headers();
    const token = getAuthToken();
    if (token) headers.set("Authorization", `Bearer ${token}`);

    const res = await fetch(`${API_BASE}/api/auth/me`, {
      cache: "no-store",
      headers,
      credentials: "include"
    });
    if (!res.ok) return { user: null, unread_count: 0 };
    const data = (await res.json()) as Partial<MeResponse>;
    return { user: data.user ?? null, unread_count: data.unread_count ?? 0 };
  } catch {
    return { user: null, unread_count: 0 };
  }
}

export async function login(email: string, password: string): Promise<AuthUser> {
  const res = await fetch(`${API_BASE}/api/auth/login`, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ email, password }),
  });
  const data = (await res.json().catch(() => ({}))) as { user?: AuthUser; token?: string; error?: string };
  if (!res.ok) throw new Error(data.error ?? "Login failed.");
  if (data.token) setAuthToken(data.token);
  return data.user as AuthUser;
}

export async function register(input: RegisterInput): Promise<AuthUser> {
  const res = await fetch(`${API_BASE}/api/auth/register`, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(input),
  });
  const data = (await res.json().catch(() => ({}))) as { user?: AuthUser; token?: string; error?: string };
  if (!res.ok) throw new Error(data.error ?? "Registration failed.");
  if (data.token) setAuthToken(data.token);
  return data.user as AuthUser;
}

export async function requestPasswordReset(email: string): Promise<void> {
  const res = await fetch(`${API_BASE}/api/auth/forgot-password`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ email }),
  });
  const data = (await res.json().catch(() => ({}))) as { ok?: boolean; error?: string };
  if (!res.ok) throw new Error(data.error ?? "Failed to request password reset.");
}

export async function resetPassword(token: string, newPassword: string): Promise<void> {
  const res = await fetch(`${API_BASE}/api/auth/reset-password`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ token, newPassword }),
  });
  const data = (await res.json().catch(() => ({}))) as { ok?: boolean; error?: string };
  if (!res.ok) throw new Error(data.error ?? "Failed to reset password.");
}

export async function logout(): Promise<void> {
  const headers = new Headers();
  const token = getAuthToken();
  if (token) headers.set("Authorization", `Bearer ${token}`);
  await fetch(`${API_BASE}/api/auth/logout`, { method: "POST", headers, credentials: "include" });
  setAuthToken(null);
}

export async function updateProfile(input: UpdateProfileInput): Promise<AuthUser> {
  const headers = new Headers({ "Content-Type": "application/json" });
  const token = getAuthToken();
  if (token) headers.set("Authorization", `Bearer ${token}`);

  const res = await fetch(`${API_BASE}/api/users/me`, {
    method: "PATCH",
    credentials: "include",
    headers,
    body: JSON.stringify(input),
  });
  profileCache.clear();
  const data = (await res.json().catch(() => ({}))) as { user?: AuthUser; error?: string };
  if (!res.ok) throw new Error(data.error ?? "Update failed.");
  return data.user as AuthUser;
}

/** Upload a profile photo; returns the updated user (with the new avatar_url). */
export async function uploadAvatar(file: File): Promise<AuthUser> {
  const fd = new FormData();
  fd.set("avatar", file);

  const headers = new Headers();
  const token = getAuthToken();
  if (token) headers.set("Authorization", `Bearer ${token}`);

  const res = await fetch(`${API_BASE}/api/users/me/avatar`, {
    method: "POST",
    credentials: "include",
    headers,
    body: fd,
  });
  profileCache.clear();
  const data = (await res.json().catch(() => ({}))) as { user?: AuthUser; error?: string };
  if (!res.ok) throw new Error(data.error ?? "Avatar upload failed.");
  return data.user as AuthUser;
}

/** Full-page navigation (anchor href) — not a fetch. */
export const googleLoginUrl = () => {
  if (typeof window !== "undefined") {
    return `${API_BASE}/api/auth/google?returnTo=${encodeURIComponent(window.location.origin)}`;
  }
  return `${API_BASE}/api/auth/google`;
};

// ---- Admin (allowlist; cookie-authenticated) ----
import type {
  AdminUserDetail,
  AdminSighting,
  AdminUser,
  AdminDashboardStats,
  AdminGroupListItem,
  AdminGroupDetail
} from "@podlog/shared";

export const fetchAdminStats = () => authedJson<{ stats: AdminDashboardStats }>("/api/admin/stats");

export const fetchAdminSightings = (page: number, limit: number, status?: string, sort_by?: string, sort_order?: string) => {
  const params = new URLSearchParams({ page: page.toString(), limit: limit.toString() });
  if (status) params.set("status", status);
  if (sort_by) params.set("sort_by", sort_by);
  if (sort_order) params.set("sort_order", sort_order);
  return authedJson<{ data: AdminSighting[]; total: number }>(`/api/admin/sightings?${params.toString()}`);
};

export const fetchAdminSighting = (id: string) => authedJson<{ sighting: AdminSighting }>(`/api/admin/sightings/${encodeURIComponent(id)}`);

export const updateAdminSightingStatus = (id: string, action: "approve" | "reject") => 
  authedJson<{ id: string; status: string }>(`/api/admin/sightings/${encodeURIComponent(id)}/action`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ action }),
  });

export const deleteAdminSighting = (id: string) =>
  authedJson<{ success: boolean }>(`/api/admin/sightings/${encodeURIComponent(id)}`, {
    method: "DELETE",
  });

export const fetchAdminUsers = (page: number, limit: number, search?: string) => {
  const params = new URLSearchParams({ page: page.toString(), limit: limit.toString() });
  if (search) params.set("search", search);
  return authedJson<{ data: AdminUser[]; total: number }>(`/api/admin/users?${params.toString()}`);
};

export const fetchAdminUser = (id: string) => authedJson<{ user: AdminUserDetail }>(`/api/admin/users/${encodeURIComponent(id)}`);

export const deleteAdminUser = (id: string) =>
  authedJson<{ success: boolean }>(`/api/admin/users/${encodeURIComponent(id)}`, {
    method: "DELETE",
  });

export const fetchAdminUserSightings = (id: string, page: number, limit: number) => {
  const params = new URLSearchParams({ page: page.toString(), limit: limit.toString() });
  return authedJson<{ data: AdminSighting[]; total: number }>(`/api/admin/users/${encodeURIComponent(id)}/sightings?${params.toString()}`);
};

export const warnAdminUser = (id: string, category: string, reason: string) => 
  authedJson<{ action: any }>(`/api/admin/users/${encodeURIComponent(id)}/warn`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ category, reason }),
  });

export const banAdminUser = (id: string, reason: string, duration_days?: number) => 
  authedJson<{ action: any }>(`/api/admin/users/${encodeURIComponent(id)}/ban`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ reason, duration_days: duration_days || undefined }),
  });

export const unbanAdminUser = (id: string) => 
  authedJson<{ action: any }>(`/api/admin/users/${encodeURIComponent(id)}/unban`, {
    method: "POST",
  });

export const toggleAdminUser = (id: string, is_admin: boolean) => 
  authedJson<{ action: any }>(`/api/admin/users/${encodeURIComponent(id)}/admin`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ is_admin }),
  });

export const previewNewsletter = (subject: string, body: string) => 
  authedJson<{ html: string }>("/api/admin/newsletter/preview", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ subject, body }),
  });

export const sendNewsletter = (subject: string, body: string) => 
  authedJson<{ newsletter: any }>("/api/admin/newsletter/send", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ subject, body }),
  });

export const fetchNewsletterHistory = (limit: number = 20) => 
  authedJson<{ history: any[] }>(`/api/admin/newsletter/history?limit=${limit}`);

export const fetchNewsletterRecipientCount = () =>
  authedJson<{ count: number }>("/api/admin/newsletter/recipient-count");

// ---- Admin: sighting media editing ----
export interface AdminSightingMediaPatch {
  photo?: Blob | null;
  remove_media?: boolean;
  youtube_url?: string; // omit to leave unchanged; "" to clear
}

export async function updateAdminSightingMedia(
  id: string,
  patch: AdminSightingMediaPatch,
): Promise<{ id: string; media_url: string | null; youtube_url: string | null }> {
  const fd = new FormData();
  if (patch.photo) fd.set("photo", patch.photo);
  if (patch.remove_media) fd.set("remove_media", "true");
  if (patch.youtube_url !== undefined) fd.set("youtube_url", patch.youtube_url);

  const headers = new Headers();
  const token = getAuthToken();
  if (token) headers.set("Authorization", `Bearer ${token}`);

  const res = await fetch(`${API_BASE}/api/admin/sightings/${encodeURIComponent(id)}/media`, {
    method: "PATCH",
    body: fd,
    credentials: "include",
    headers,
  });
  const data = (await res.json().catch(() => ({}))) as any;
  if (!res.ok) throw new Error(data.error ?? `Media update failed (${res.status})`);
  return data;
}

// ---- Admin: group moderation ----
export const fetchAdminGroups = (page: number, limit: number, search?: string, sort_by?: string, sort_order?: string) => {
  const params = new URLSearchParams({ page: page.toString(), limit: limit.toString() });
  if (search) params.set("search", search);
  if (sort_by) params.set("sort_by", sort_by);
  if (sort_order) params.set("sort_order", sort_order);
  return authedJson<{ data: AdminGroupListItem[]; total: number }>(`/api/admin/groups?${params.toString()}`);
};

export const fetchAdminGroup = (id: string) =>
  authedJson<{ group: AdminGroupDetail }>(`/api/admin/groups/${encodeURIComponent(id)}`);

export const updateAdminGroup = (
  id: string,
  patch: { name?: string; description?: string | null; visibility?: string; join_policy?: string },
) =>
  authedJson<{ ok: boolean }>(`/api/admin/groups/${encodeURIComponent(id)}`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(patch),
  });

export const deleteAdminGroup = (id: string) =>
  authedJson<{ ok: boolean }>(`/api/admin/groups/${encodeURIComponent(id)}`, { method: "DELETE" });

export const setAdminGroupMemberRole = (id: string, userId: string, role: GroupRole) =>
  authedJson<{ ok: boolean; transferred?: boolean }>(
    `/api/admin/groups/${encodeURIComponent(id)}/members/${encodeURIComponent(userId)}`,
    {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ role }),
    },
  );

export const removeAdminGroupMember = (id: string, userId: string, ban = false) => {
  const q = ban ? "?ban=1" : "";
  return authedJson<{ ok: boolean }>(
    `/api/admin/groups/${encodeURIComponent(id)}/members/${encodeURIComponent(userId)}${q}`,
    { method: "DELETE" },
  );
};

export const fetchAdminGroupPostComments = (postId: string) =>
  authedJson<{ comments: GroupComment[] }>(`/api/admin/groups/posts/${encodeURIComponent(postId)}/comments`);

export const deleteAdminGroupPost = (postId: string) =>
  authedJson<{ ok: boolean }>(`/api/admin/groups/posts/${encodeURIComponent(postId)}`, { method: "DELETE" });

export const pinAdminGroupPost = (postId: string, pinned: boolean) =>
  authedJson<{ ok: boolean }>(`/api/admin/groups/posts/${encodeURIComponent(postId)}/pin`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ pinned }),
  });

export const deleteAdminGroupComment = (commentId: string) =>
  authedJson<{ ok: boolean }>(`/api/admin/groups/comments/${encodeURIComponent(commentId)}`, { method: "DELETE" });

export interface SightingReporterAdmin {
  sighting_id: string;
  user_id: string | null;
  username: string | null;
  display_name: string | null;
  avatar_url: string | null;
  is_anonymous: boolean;
}

function adminHeaders(): Headers {
  const headers = new Headers();
  const token = getAuthToken();
  if (token) headers.set("Authorization", `Bearer ${token}`);
  return headers;
}


// ---- Public profile (cookie-authenticated; 404 if no such user) ----
export interface PublicProfileResponse {
  profile: {
    id: string;
    username: string;
    display_name: string | null;
    avatar_url: string | null;
    bio: string | null;
    privacy: "public" | "friends_only" | "private";
    created_at: string;
  };
  restricted: boolean;
  isOwner?: boolean;
  friendship?: FriendshipStatus | null;
  stats?: { total_sightings: number; unique_species: number; lifers: number };
  species?: SpeciesType[];
  recent?: Array<{
    sighting_id: string;
    species_type: SpeciesType;
    lat: number;
    lng: number;
    timestamp: string;
    media_url: string | null;
    youtube_url: string | null;
    is_lifer: boolean;
  }>;
}

const profileCache = new Map<string, { data: PublicProfileResponse | null; ts: number }>();

/** Returns null on 404 (no such user). */
export async function fetchPublicProfile(username: string): Promise<PublicProfileResponse | null> {
  const cached = profileCache.get(username);
  if (cached && Date.now() - cached.ts < CACHE_TTL) return cached.data;

  const headers = new Headers();
  const token = getAuthToken();
  if (token) headers.set("Authorization", `Bearer ${token}`);

  const res = await fetch(`${API_BASE}/api/users/${encodeURIComponent(username)}`, {
    headers,
    credentials: "include",
  });
  if (res.status === 404) {
    profileCache.set(username, { data: null, ts: Date.now() });
    return null;
  }
  if (!res.ok) throw new Error(`profile → ${res.status}`);
  const data = (await res.json()) as PublicProfileResponse;
  profileCache.set(username, { data, ts: Date.now() });
  return data;
}

// ---- Friends (cookie-authenticated) ----
export interface FriendSummary {
  id: string;
  username: string;
  display_name: string | null;
  avatar_url: string | null;
  bio: string | null;
  privacy: "public" | "friends_only" | "private";
  created_at: string;
  friendship_id: string;
  requested_at: string;
}

export interface FriendsResponse {
  friends: FriendSummary[];
  incoming: FriendSummary[];
  outgoing: FriendSummary[];
}

const friendsCache = new Map<string, { data: FriendsResponse; ts: number }>();

export async function fetchFriends(): Promise<FriendsResponse> {
  const cached = friendsCache.get("all");
  if (cached && Date.now() - cached.ts < CACHE_TTL) return cached.data;

  const data = await authedJson<FriendsResponse>("/api/friends");
  friendsCache.set("all", { data, ts: Date.now() });
  return data;
}

export const sendFriendRequest = async (username: string) => {
  friendsCache.clear();
  return authedJson<{ id: string; status: string }>("/api/friends/request", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ username }),
  });
};

export const respondToFriend = async (friendshipId: string, action: "accept" | "decline" | "block") => {
  friendsCache.clear();
  return authedJson<{ ok: boolean; status: string }>(`/api/friends/${friendshipId}/respond`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ action }),
  });
};

export interface UserSearchResult {
  id: string;
  username: string;
  display_name: string | null;
  avatar_url: string | null;
  friendship_status: "pending" | "accepted" | "declined" | "blocked" | null;
}

export async function searchUsers(query: string): Promise<UserSearchResult[]> {
  const data = await authedJson<{ users: UserSearchResult[] }>(`/api/friends/search?q=${encodeURIComponent(query)}`);
  return data.users;
}

// ---- Notifications (cookie-authenticated) ----

const notificationsCache = new Map<string, { data: any; ts: number }>();

export async function fetchAlertZones(): Promise<AlertZone[]> {
  const cached = notificationsCache.get("zones");
  if (cached && Date.now() - cached.ts < CACHE_TTL) return cached.data;

  const headers = new Headers();
  const token = getAuthToken();
  if (token) headers.set("Authorization", `Bearer ${token}`);

  const res = await fetch(`${API_BASE}/api/notifications/zones`, { 
    cache: "no-store",
    headers, 
    credentials: "include" 
  });
  if (res.status === 401) throw new UnauthorizedError();
  if (!res.ok) throw new Error("Failed to load alert zones");
  const data = (await res.json()).zones as AlertZone[];
  notificationsCache.set("zones", { data, ts: Date.now() });
  return data;
}

export async function createAlertZone(input: AlertZoneInput): Promise<AlertZone> {
  notificationsCache.delete("zones");
  const headers = new Headers({ "content-type": "application/json" });
  const token = getAuthToken();
  if (token) headers.set("Authorization", `Bearer ${token}`);

  const res = await fetch(`${API_BASE}/api/notifications/zones`, {
    method: "POST",
    credentials: "include",
    headers,
    body: JSON.stringify(input),
  });
  if (res.status === 401) throw new UnauthorizedError();
  if (!res.ok) throw new Error((await res.json().catch(() => ({})))?.error ?? "Failed to create zone");
  return (await res.json()).zone as AlertZone;
}

export async function deleteAlertZone(id: string): Promise<void> {
  notificationsCache.delete("zones");
  const headers = new Headers();
  const token = getAuthToken();
  if (token) headers.set("Authorization", `Bearer ${token}`);

  const res = await fetch(`${API_BASE}/api/notifications/zones/${id}`, {
    method: "DELETE",
    headers,
    credentials: "include",
  });
  if (res.status === 401) throw new UnauthorizedError();
  if (!res.ok) throw new Error("Failed to delete zone");
}

export async function updateAlertZone(id: string, active: boolean): Promise<AlertZone> {
  notificationsCache.delete("zones");
  const headers = new Headers({ "content-type": "application/json" });
  const token = getAuthToken();
  if (token) headers.set("Authorization", `Bearer ${token}`);

  const res = await fetch(`${API_BASE}/api/notifications/zones/${id}`, {
    method: "PATCH",
    credentials: "include",
    headers,
    body: JSON.stringify({ active }),
  });
  if (res.status === 401) throw new UnauthorizedError();
  if (!res.ok) throw new Error("Failed to update zone");
  return (await res.json()).zone as AlertZone;
}

export async function fetchNotificationPrefs(): Promise<NotificationPreferences> {
  const cached = notificationsCache.get("prefs");
  if (cached && Date.now() - cached.ts < CACHE_TTL) return cached.data;

  const headers = new Headers();
  const token = getAuthToken();
  if (token) headers.set("Authorization", `Bearer ${token}`);

  const res = await fetch(`${API_BASE}/api/notifications/prefs`, { 
    cache: "no-store",
    headers, 
    credentials: "include" 
  });
  if (res.status === 401) throw new UnauthorizedError();
  if (!res.ok) throw new Error("Failed to load preferences");
  const data = (await res.json()).prefs as NotificationPreferences;
  notificationsCache.set("prefs", { data, ts: Date.now() });
  return data;
}

export async function updateNotificationPrefs(
  input: Partial<Pick<NotificationPreferences, "email_social" | "newsletter_opt_in">>,
): Promise<NotificationPreferences> {
  notificationsCache.delete("prefs");
  const headers = new Headers({ "content-type": "application/json" });
  const token = getAuthToken();
  if (token) headers.set("Authorization", `Bearer ${token}`);

  const res = await fetch(`${API_BASE}/api/notifications/prefs`, {
    method: "PUT",
    credentials: "include",
    headers,
    body: JSON.stringify(input),
  });
  if (res.status === 401) throw new UnauthorizedError();
  if (!res.ok) throw new Error("Failed to save preferences");
  return (await res.json()).prefs as NotificationPreferences;
}

// ---- In-app notification feed / the header bell (cookie-authenticated) ----

export interface NotificationsResponse {
  notifications: AppNotification[];
  unread_count: number;
}

/** The current user's notifications + unread count (opens the bell panel). */
export function fetchNotifications(): Promise<NotificationsResponse> {
  return authedJson<NotificationsResponse>("/api/inbox");
}

/** Lightweight unread count — polled by the bell badge. */
export async function fetchUnreadCount(): Promise<number> {
  const data = await authedJson<{ count: number }>("/api/inbox/unread-count");
  return data.count;
}

/** Mark every notification read (called when the panel opens). */
export async function markAllNotificationsRead(): Promise<void> {
  await authedJson("/api/inbox/read-all", { method: "POST" });
}

/** Mark a single notification read. */
export async function markNotificationRead(id: string): Promise<void> {
  await authedJson(`/api/inbox/${encodeURIComponent(id)}/read`, { method: "POST" });
}

// ---- Community groups (cookie-authenticated) ----

/** Discover public groups. Optional search term matches name/description. */
export async function listPublicGroups(search?: string): Promise<Group[]> {
  const params = new URLSearchParams();
  if (search) params.set("search", search);
  const qs = params.toString();
  const data = await authedJson<{ groups: Group[] }>(`/api/groups${qs ? `?${qs}` : ""}`);
  return data.groups;
}

/** The signed-in user's groups, annotated with their role. */
export async function listMyGroups(): Promise<GroupSummary[]> {
  const data = await authedJson<{ groups: GroupSummary[] }>("/api/groups/mine");
  return data.groups;
}

export async function getGroup(slug: string): Promise<GroupSummary> {
  const data = await authedJson<{ group: GroupSummary }>(`/api/groups/${encodeURIComponent(slug)}`);
  return data.group;
}

/** Create a group; `icon` is an optional (client-compressed) image File. */
export async function createGroup(input: CreateGroupInput & { icon?: File | null }): Promise<GroupSummary> {
  const fd = new FormData();
  fd.set("name", input.name);
  if (input.description) fd.set("description", input.description);
  if (input.visibility) fd.set("visibility", input.visibility);
  if (input.join_policy) fd.set("join_policy", input.join_policy);
  if (input.icon) fd.set("icon", input.icon);
  const data = await authedJson<{ group: GroupSummary }>("/api/groups", { method: "POST", body: fd });
  return data.group;
}

export async function updateGroup(
  slug: string,
  patch: UpdateGroupInput & { icon?: File | null },
): Promise<GroupSummary> {
  const fd = new FormData();
  if (patch.name !== undefined) fd.set("name", patch.name);
  if (patch.description !== undefined) fd.set("description", patch.description ?? "");
  if (patch.visibility !== undefined) fd.set("visibility", patch.visibility);
  if (patch.join_policy !== undefined) fd.set("join_policy", patch.join_policy);
  if (patch.icon) fd.set("icon", patch.icon);
  const data = await authedJson<{ group: GroupSummary }>(`/api/groups/${encodeURIComponent(slug)}`, {
    method: "PATCH",
    body: fd,
  });
  return data.group;
}

export async function deleteGroup(slug: string): Promise<void> {
  await authedJson(`/api/groups/${encodeURIComponent(slug)}`, { method: "DELETE" });
}

// ---- Groups: membership, invites & join-requests ----

export interface GroupJoinRequest {
  id: string;
  created_at: string;
  user: { user_id: string; username: string; display_name: string | null; avatar_url: string | null };
}

export async function listGroupMembers(slug: string): Promise<GroupMember[]> {
  const data = await authedJson<{ members: GroupMember[] }>(
    `/api/groups/${encodeURIComponent(slug)}/members`,
  );
  return data.members;
}

/** The group's sightings map (members' verified sightings, group-attributed). */
export async function fetchGroupMap(slug: string): Promise<FeatureCollection> {
  return authedJson<FeatureCollection>(`/api/groups/${encodeURIComponent(slug)}/map`);
}

export async function fetchGroupLeaderboard(slug: string): Promise<GroupLeaderboardEntry[]> {
  const data = await authedJson<{ leaderboard: GroupLeaderboardEntry[] }>(
    `/api/groups/${encodeURIComponent(slug)}/leaderboard`,
  );
  return data.leaderboard;
}

// ---- Groups: the community wall (poll-based; never cached) ----

export async function fetchGroupPosts(slug: string, before?: string): Promise<GroupPost[]> {
  const qs = before ? `?before=${encodeURIComponent(before)}` : "";
  const data = await authedJson<{ posts: GroupPost[] }>(
    `/api/groups/${encodeURIComponent(slug)}/posts${qs}`,
  );
  return data.posts;
}

export async function createGroupPost(
  slug: string,
  input: { body?: string; image?: File | null },
): Promise<GroupPost | null> {
  const fd = new FormData();
  if (input.body) fd.set("body", input.body);
  if (input.image) fd.set("image", input.image);
  const data = await authedJson<{ post: GroupPost | null }>(
    `/api/groups/${encodeURIComponent(slug)}/posts`,
    { method: "POST", body: fd },
  );
  return data.post;
}

export async function deleteGroupPost(postId: string): Promise<void> {
  await authedJson(`/api/groups/posts/${postId}`, { method: "DELETE" });
}

export async function pinGroupPost(postId: string, pinned: boolean): Promise<void> {
  await authedJson(`/api/groups/posts/${postId}/pin`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ pinned }),
  });
}

export async function fetchGroupComments(postId: string): Promise<GroupComment[]> {
  const data = await authedJson<{ comments: GroupComment[] }>(`/api/groups/posts/${postId}/comments`);
  return data.comments;
}

export async function addGroupComment(postId: string, body: string): Promise<GroupComment> {
  const data = await authedJson<{ comment: GroupComment }>(`/api/groups/posts/${postId}/comments`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ body }),
  });
  return data.comment;
}

export async function deleteGroupComment(commentId: string): Promise<void> {
  await authedJson(`/api/groups/comments/${commentId}`, { method: "DELETE" });
}

export async function setGroupReaction(postId: string, emoji: GroupReactionEmoji): Promise<void> {
  await authedJson(`/api/groups/posts/${postId}/reaction`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ emoji }),
  });
}

export async function removeGroupReaction(postId: string): Promise<void> {
  await authedJson(`/api/groups/posts/${postId}/reaction`, { method: "DELETE" });
}

/** Join (open) or request to join a group. Returns the resulting state. */
export async function joinGroup(slug: string): Promise<"member" | "requested"> {
  const data = await authedJson<{ status: "member" | "requested" }>(
    `/api/groups/${encodeURIComponent(slug)}/join`,
    { method: "POST" },
  );
  return data.status;
}

export async function leaveGroup(slug: string): Promise<void> {
  await authedJson(`/api/groups/${encodeURIComponent(slug)}/leave`, { method: "POST" });
}

export async function removeGroupMember(slug: string, userId: string, ban = false): Promise<void> {
  const qs = ban ? "?ban=1" : "";
  await authedJson(`/api/groups/${encodeURIComponent(slug)}/members/${userId}${qs}`, {
    method: "DELETE",
  });
}

/** Set a member's role, or transfer ownership (role="owner"). */
export async function setGroupMemberRole(slug: string, userId: string, role: GroupRole): Promise<void> {
  await authedJson(`/api/groups/${encodeURIComponent(slug)}/members/${userId}`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ role }),
  });
}

export async function inviteGroupMember(slug: string, username: string): Promise<void> {
  await authedJson(`/api/groups/${encodeURIComponent(slug)}/invites`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ username }),
  });
}

export async function listIncomingGroupInvites(): Promise<GroupInvite[]> {
  const data = await authedJson<{ invites: GroupInvite[] }>("/api/groups/invites/incoming");
  return data.invites;
}

export async function respondGroupInvite(id: string, action: "accept" | "decline"): Promise<void> {
  await authedJson(`/api/groups/invites/${id}/respond`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ action }),
  });
}

export async function listGroupJoinRequests(slug: string): Promise<GroupJoinRequest[]> {
  const data = await authedJson<{ requests: GroupJoinRequest[] }>(
    `/api/groups/${encodeURIComponent(slug)}/requests`,
  );
  return data.requests;
}

export async function respondGroupJoinRequest(
  slug: string,
  id: string,
  action: "approve" | "decline",
): Promise<void> {
  await authedJson(`/api/groups/${encodeURIComponent(slug)}/requests/${id}/respond`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ action }),
  });
}
